import { useQuery } from 'react-query';

import { GetPersonsResponse } from '../../../../backend/src/case/person/dto/get-persons-response.dto';
import { APIBaseChronos } from '@/api/hosts';
import { QueryEntity } from '@/api/types';
import { Person } from '@/api/types/backend-dto-types';
import useGetFetchConfig from '@/api/useGetFetchConfig';
import { PersonFiltersV2 } from '@/types';

interface GetPersonsReq {
  caseId: string | null;
  searchQuery?: string;
  page?: number;
  pageSize?: number;
  paginated?: boolean;
  filters?: PersonFiltersV2;
}

// Conditional type for return value based on paginated parameter
type GetPersonsReturnType<T extends GetPersonsReq> = T['paginated'] extends false
  ? ReturnType<typeof useQuery<Person[], Error>>
  : ReturnType<typeof useQuery<GetPersonsResponse, Error>>;

export function useGetPersons<T extends GetPersonsReq>(params: T): GetPersonsReturnType<T> {
  const { caseId, searchQuery, page, pageSize, paginated, filters } = params;
  const { fetchConfigGET } = useGetFetchConfig();

  return useQuery<GetPersonsReturnType<T>, Error>(
    [QueryEntity.Persons, { caseId }, { page, pageSize, paginated, filters }, { searchQuery }],
    async () => {
      const urlParams = new URLSearchParams();

      function addParam(key: string, value: unknown) {
        if (value === null || value === undefined) return;
        urlParams.append(key, String(value));
      }

      addParam('searchQuery', searchQuery);
      addParam('page', page);
      addParam('pageSize', pageSize);
      addParam('paginated', paginated);

      if (filters?.personType) {
        for (const filter of filters.personType) {
          addParam('personType[]', filter);
        }
      }

      const response = await fetch(
        `${APIBaseChronos}/api/case/${caseId}/person?${urlParams.toString()}`,
        fetchConfigGET,
      );

      if (!response.ok) throw new Error('Network response was not ok');
      return response.json();
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!caseId,
    },
  ) as GetPersonsReturnType<T>;
}
